import React, { useRef, useState, useLayoutEffect, useCallback } from "react";
import {
  ScatterChart as RechartsScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Component, ChartConfig } from "@/types";
import { getChartSizeConfig, getChartResponsiveConfig } from "@/utils/chart-responsive-utils";
import { useResponsiveViewport } from "@/hooks/useResponsiveViewport";
import { useQueryTemplateData, isQueryTemplateDataSource, getXAxisKeyFromQuery, getLineKeysFromQuery } from "@/hooks/useQueryTemplateData";
import { ChartDataSource } from "@/types/chartDataSource";
import { resolveColorArray } from "@/utils/cssVariableResolver";
import { AlertCircle, RefreshCw } from "lucide-react";
import { useChartNavigation } from "@/hooks/useChartNavigation";
import { useComponentInteraction } from "@/components/shared/rendering/InteractionContextRuntime";
import { NavigationIndicator } from "./NavigationIndicator";
import { useChartHoverStore } from "@/stores/chart/chart-hover-store";
import { normalizeChartRows } from "./chartDataUtils";
import { ChartLoadingSkeleton } from "@/components/common/DataLoadingSkeletons";

interface ScatterChartProps {
  component: Component;
  isPreview?: boolean;
}

interface ScatterMouseMoveEvent {
  activePayload?: Array<{ payload?: Record<string, unknown> }>;
  activeTooltipIndex?: number | null;
}

interface ScatterClickPayload {
  payload?: Record<string, unknown>;
  index?: number;
}

interface LegendValueObject {
  name?: string;
  key?: string;
}

const toNumericDimension = (value: unknown): number | undefined =>
  typeof value === "number" ? value : undefined;

const defaultData = [
  { x: 100, y: 200, z: 200 },
  { x: 120, y: 100, z: 260 },
  { x: 170, y: 300, z: 400 },
  { x: 140, y: 250, z: 280 },
  { x: 150, y: 400, z: 500 },
  { x: 110, y: 280, z: 200 },
];

const defaultColors = [
  "#8884d8",
  "#82ca9d",
  "#ffc658",
  "#ff7300",
  "#8dd1e1",
  "#d084d0",
];

export const ScatterChart: React.FC<ScatterChartProps> = ({
  component,
  isPreview = false,
}) => {
  const { properties = {}, style = {}, size, responsivePosition } = component;
  const containerRef = useRef<HTMLDivElement>(null);
  const breakpoint = useResponsiveViewport();

  // Track palette changes via CSS variable mutations
  const [paletteChangeCounter, setPaletteChangeCounter] = useState(0);

  // Merge breakpoint-specific properties with base properties
  const breakpointProps = responsivePosition?.breakpoints?.[breakpoint]?.properties || {};
  const mergedProperties = { ...properties, ...breakpointProps };

  // Get dataSource configuration
  const dataSource = mergedProperties.dataSource as ChartDataSource | undefined;
  const templateId = mergedProperties.queryTemplateId as string | undefined;
  const hasQueryTemplate = isQueryTemplateDataSource(dataSource);

  // Execute Query Template if configured
  const {
    data: queryTemplateData,
    isLoading: qtLoading,
    error: qtError,
    refetch: qtRefetch,
    lastFetched,
  } = useQueryTemplateData({
    dataSource,
    componentId: component.id,
    enabled: hasQueryTemplate || !!templateId,
    templateId,
    initialData: mergedProperties.data as Record<string, unknown>[] | undefined,
  });

  // Loading/error state from query template
  const loading = qtLoading;
  const error = qtError;

  // Data priority: Query template > Static data > Default data
  const chartData = queryTemplateData
    || (mergedProperties.data && Array.isArray(mergedProperties.data)
      ? mergedProperties.data
      : defaultData);

  const normalizedChartData = normalizeChartRows(chartData as Record<string, unknown>[]);

  // Determine axis keys - validate against actual data, auto-detect if needed
  const queryMeasureKeys = hasQueryTemplate && dataSource?.queryDefinition ? getLineKeysFromQuery(dataSource.queryDefinition) : [];
  const validatedMeasureKeys = queryMeasureKeys.length > 0 && normalizedChartData.length > 0
    ? queryMeasureKeys.filter(k => k in normalizedChartData[0])
    : queryMeasureKeys;
  // Validate explicit keys against actual data — fall through if they don't match
  const explicitMeasureKeys = Array.isArray(mergedProperties.lineKeys)
    ? mergedProperties.lineKeys.filter((key): key is string => typeof key === 'string')
    : null;
  const validatedExplicitMeasureKeys = explicitMeasureKeys && normalizedChartData.length > 0
    ? explicitMeasureKeys.filter(k => k in normalizedChartData[0])
    : explicitMeasureKeys;
  const measureKeys = (validatedExplicitMeasureKeys && validatedExplicitMeasureKeys.length > 0 ? validatedExplicitMeasureKeys : null)
    || (validatedMeasureKeys.length > 0 ? validatedMeasureKeys : null)
    || (normalizedChartData.length > 0
        ? Object.keys(normalizedChartData[0]).filter(key => typeof normalizedChartData[0][key] === 'number')
        : []);

  const queryXKey = hasQueryTemplate && dataSource?.queryDefinition ? getXAxisKeyFromQuery(dataSource.queryDefinition) : null;
  const autoXKey = normalizedChartData.length > 0
    ? Object.keys(normalizedChartData[0]).find(k => typeof normalizedChartData[0][k] === 'string' || k.includes('date') || k.includes('time'))
    : null;
  const xKey = (mergedProperties.xAxisKey as string)
    || (queryXKey && normalizedChartData.length > 0 && queryXKey in normalizedChartData[0] ? queryXKey : null)
    || autoXKey
    || queryXKey
    || "x";

  const yKey = (mergedProperties.yAxisKey as string)
    || (measureKeys.length > 0 ? measureKeys[0] : null)
    || "y";

  // Optional: zKey for bubble size (second measure if available)
  const zKey = (mergedProperties.zAxisKey as string)
    || (measureKeys.length > 1 ? measureKeys[1] : null)
    || undefined;

  // Get size configuration from component.size (no ResizeObserver needed)
  const sizeConfig = getChartSizeConfig(
    toNumericDimension(size?.width),
    toNumericDimension(size?.height)
  );
  const { isSmall } = sizeConfig;

  // Get responsive configuration
  const title = mergedProperties.title as string | undefined;
  const description = mergedProperties.description as string;
  const userMargin = mergedProperties.margin as ChartConfig['margin'];

  const responsiveConfig = getChartResponsiveConfig(
    sizeConfig,
    !!title,
    !!description,
    userMargin,
    mergedProperties.showLegend !== false,
    mergedProperties.showGrid !== false
  );

  // Resolve CSS variables in colors to actual color values for Recharts
  const [resolvedColors, setResolvedColors] = useState<string[]>(() => {
    const colors = (mergedProperties.colors as string[]) || defaultColors;
    return colors;
  });

  // Watch for CSS variable changes on ancestor with data-palette-scope
  useLayoutEffect(() => {
    const findPaletteScope = (element: HTMLElement | null): HTMLElement | null => {
      while (element) {
        if (element.dataset.paletteScope === 'true') return element;
        element = element.parentElement;
      }
      return null;
    };

    const paletteScope = findPaletteScope(containerRef.current);
    if (!paletteScope) return;

    const observer = new MutationObserver(() => {
      setPaletteChangeCounter(c => c + 1);
    });

    observer.observe(paletteScope, {
      attributes: true,
      attributeFilter: ['style']
    });

    return () => observer.disconnect();
  }, []);

  // Resolve colors after DOM is mounted to ensure palette scope CSS variables are accessible
  // Re-resolve when palette changes to update CSS variable values
  useLayoutEffect(() => {
    const colors = (mergedProperties.colors as string[]) || defaultColors;
    const resolved = resolveColorArray(colors, containerRef.current);
    setResolvedColors(resolved);
  }, [mergedProperties.colors, paletteChangeCounter]);

  const chartConfig: ChartConfig = {
    title,
    data: normalizedChartData,
    xAxisKey: xKey,
    yAxisKey: yKey,
    showLegend: responsiveConfig.showLegend,
    showTooltip: mergedProperties.showTooltip !== false,
    showGrid: responsiveConfig.showGrid,
    colors: resolvedColors,
    responsive: mergedProperties.responsive !== false,
    animation: mergedProperties.animation !== false,
    margin: responsiveConfig.margin,
  };

  // Chart navigation hooks for click-through navigation
  const interaction = useComponentInteraction(component);
  const { handleChartClick, isNavigable, getNavigationForElement } = useChartNavigation(component, interaction);

  // Chart hover store handlers for compound chart integration
  const setHoverData = useChartHoverStore((state) => state.setHoverData);
  const clearHoverData = useChartHoverStore((state) => state.clearHoverData);

  const handleChartMouseMove = useCallback(
    (event: ScatterMouseMoveEvent) => {
      if (event.activePayload?.[0]?.payload) {
        setHoverData(
          component.id,
          event.activePayload[0].payload,
          event.activeTooltipIndex ?? null
        );
      }
    },
    [component.id, setHoverData]
  );

  const handleChartMouseLeave = useCallback(() => {
    clearHoverData(component.id);
  }, [component.id, clearHoverData]);

  // Determine if scatter has navigation configured
  const scatterNavigation = getNavigationForElement(xKey);
  const hasScatterNavigation = isNavigable && scatterNavigation;

  // Calculate minimum height for the chart area
  const dynamicMinimum = sizeConfig.isVerySmall ? 50 : sizeConfig.isSmall ? 80 : 100;

  // Container style - let it fill the wrapper
  // Strip width-related properties from component.style - sizing is handled by ComponentWrapper/FlexContainer
  const { width: _w, minWidth: _mw, maxWidth: _xw, ...visualStyle } = style as React.CSSProperties;
  const containerStyle: React.CSSProperties = {
    ...visualStyle,
    position: 'relative',
    height: '100%',
    width: '100%',
    minHeight: (typeof mergedProperties.minHeight === 'string' || typeof mergedProperties.minHeight === 'number')
      ? mergedProperties.minHeight
      : `${dynamicMinimum + (responsiveConfig.showTitle ? 40 : 0)}px`,
    display: 'flex',
    flexDirection: 'column',
  };

  return (
    <div className="w-full h-full" style={containerStyle} ref={containerRef}>
      <NavigationIndicator isNavigable={isNavigable && !isPreview} />
      {responsiveConfig.showTitle && (
        <div className={`text-center ${responsiveConfig.titleMargin} relative`}>
          <h3 className={responsiveConfig.titleClass}>{chartConfig.title}</h3>
          {responsiveConfig.showDescription && (
            <p className="text-xs text-muted-foreground">
              {description}
            </p>
          )}
          {/* Refresh button for query template data sources */}
          {hasQueryTemplate && !isPreview && (
            <button
              onClick={() => qtRefetch()}
              className="absolute right-2 top-0 p-1 rounded hover:bg-muted/50 transition-colors"
              title={lastFetched ? `Last updated: ${lastFetched.toLocaleTimeString()}` : 'Refresh data'}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 text-muted-foreground ${loading ? 'animate-spin' : ''}`} />
            </button>
          )}
        </div>
      )}

      <div style={{ position: 'relative', width: '100%', flex: 1, minHeight: 0 }}>
        {loading ? (
          <ChartLoadingSkeleton variant="scatter" />
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full space-y-2">
            <AlertCircle className="w-8 h-8 text-red-500" />
            <p className="text-sm text-red-600">{error}</p>
          </div>
        ) : chartData && chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%" minWidth={0} debounce={50}>
            <RechartsScatterChart
              data={chartConfig.data}
              margin={chartConfig.margin}
              style={{ userSelect: isPreview ? "auto" : "none" }}
              onMouseMove={handleChartMouseMove}
              onMouseLeave={handleChartMouseLeave}
            >
            {chartConfig.showGrid && mergedProperties.showXAxis !== false && mergedProperties.showYAxis !== false && (
              <CartesianGrid strokeDasharray="3 3" />
            )}
            <XAxis
              type={xKey.toLowerCase().includes('date') || xKey.toLowerCase().includes('time') || xKey.toLowerCase().includes('timestamp') ? 'category' : 'number'}
              dataKey={chartConfig.xAxisKey}
              tick={mergedProperties.showXAxis === false ? false : { fontSize: responsiveConfig.axisFontSize }}
              axisLine={mergedProperties.showXAxis !== false}
              tickLine={mergedProperties.showXAxis !== false}
              domain={Array.isArray(mergedProperties.xDomain) ? mergedProperties.xDomain : ["auto", "auto"]}
              label={mergedProperties.xAxisLabel && mergedProperties.showXAxis !== false && !isSmall ? { value: String(mergedProperties.xAxisLabel), position: 'insideBottom', offset: -5, fontSize: responsiveConfig.axisFontSize } : undefined}
              tickFormatter={(value) => {
                // Format date strings for display
                if (typeof value === 'string' && value.includes('T')) {
                  const date = new Date(value);
                  return date.toLocaleDateString();
                }
                return value;
              }}
            />
            <YAxis
              type="number"
              dataKey={chartConfig.yAxisKey}
              tick={mergedProperties.showYAxis === false ? false : { fontSize: responsiveConfig.axisFontSize }}
              axisLine={mergedProperties.showYAxis !== false}
              tickLine={mergedProperties.showYAxis !== false}
              domain={Array.isArray(mergedProperties.yDomain) ? mergedProperties.yDomain : ["auto", "auto"]}
              label={mergedProperties.yAxisLabel && mergedProperties.showYAxis !== false && !isSmall ? { value: String(mergedProperties.yAxisLabel), angle: -90, position: 'insideLeft', fontSize: responsiveConfig.axisFontSize } : undefined}
            />
            {/* ZAxis for bubble size when zKey is available */}
            {zKey && (
              <ZAxis
                type="number"
                dataKey={zKey}
                range={Array.isArray(mergedProperties.zRange) ? mergedProperties.zRange : [50, 400]}
                name={zKey}
              />
            )}
            {chartConfig.showTooltip && (
              <Tooltip
                cursor={{ strokeDasharray: "3 3" }}
                contentStyle={{
                  backgroundColor: "hsl(var(--popover))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "6px",
                }}
              />
            )}
            {chartConfig.showLegend && (
              <Legend
                wrapperStyle={{ fontSize: `${responsiveConfig.legendFontSize}px` }}
                formatter={(value: unknown) => {
                  // Handle both string values and dataset objects
                  if (typeof value === 'string') return value;
                  if (typeof value === 'object' && value !== null) {
                    const legendValue = value as LegendValueObject;
                    return legendValue.name || legendValue.key || String(value);
                  }
                  return String(value);
                }}
              />
            )}
            <Scatter
              data={chartConfig.data}
              fill={chartConfig.colors?.[0] || defaultColors[0]}
              animationDuration={chartConfig.animation ? 300 : 0}
              onClick={(data: ScatterClickPayload) => {
                handleChartClick(data.payload ?? {}, xKey, data.index);
              }}
              cursor={hasScatterNavigation ? 'pointer' : 'default'}
            />
          </RechartsScatterChart>
        </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className="text-muted-foreground">No chart data available</p>
          </div>
        )}
      </div>
    </div>
  );
};
